/**
 * Property-name resolution for bidirectional relationship indexing in
 * GraphStore.
 *
 * Each model class exposes a `static relationships` map keyed by its
 * own property names, with `{ target, cardinality, isParent, relClass,
 * direction }` per entry. That map is THE source of truth — the same
 * relationship class can correspond to different property names on
 * different origin classes (polymorphic-source rels like
 * `ContainsLogicalPart`: `Project.logical_parts` vs
 * `LogicalPart.children`), and only the owning model knows its own
 * name for the relationship.
 *
 * The previous `origin_property` / `dest_property` scalars on the
 * Relationship class couldn't express this, which broke live updates
 * for any DAG patch whose source model wasn't the codegen-default
 * origin (see also fix in `dag-sync/client.ts`).
 */

interface ModelClassWithRels {
  relationships?: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }>;
}

/**
 * Find the property name on `modelClass` for the given rel-class +
 * direction. Returns null if no match (e.g. the class doesn't
 * participate in this relationship on that side).
 */
export function findRelationshipProperty(
  modelClass: ModelClassWithRels | undefined,
  relClassName: string,
  direction: 'in' | 'out',
): string | null {
  const rels = (modelClass as ModelClassWithRels | undefined)?.relationships;
  if (!rels) return null;
  for (const [propName, info] of Object.entries(rels)) {
    if (info.relClass === relClassName && info.direction === direction) {
      return propName;
    }
  }
  return null;
}

export interface RelClassMeta {
  /** Property name on the origin side, as exposed by codegen. */
  origin_property: string;
  /** Property name on the dest side, as exposed by codegen. */
  dest_property: string;
  /** All origin model class names (may be polymorphic). */
  originModelNames: readonly string[];
  /** All dest model class names (may be polymorphic). */
  destModelNames: readonly string[];
}

/**
 * Resolve the property name on a node's model for an edge of `relClassName` in
 * the given role.
 *
 * Every edge is emitted by the backend in canonical `origin -> dest`
 * orientation, so the SOURCE node always uses its `'out'` (origin) property and
 * the TARGET its `'in'` (dest) property — there is no direction to detect.
 * Type-aware via the model's own `relationships` map (so polymorphic rels like
 * `Project.actions` vs `Action.sub_actions` resolve correctly); falls back to
 * the relClass scalar property name when the model isn't available (e.g. the
 * node isn't in the store yet).
 */
export function resolveRelProperty(
  model: ModelClassWithRels | undefined,
  relClassName: string,
  role: 'out' | 'in',
  relClass?: RelClassMeta,
): string | undefined {
  return (
    findRelationshipProperty(model, relClassName, role) ??
    (role === 'out' ? relClass?.origin_property : relClass?.dest_property)
  );
}
